import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ArchbuildService} from './archbuild.service';
import {
  ArchBuildPackageBuildDto,
  ArchBuildPackageBuildStepDto,
  ArchBuildPackageCreationDto,
  ArchBuildPackageDto
} from './archbuild.models';
import {ToastrService} from 'ngx-toastr';
import { FormsModule } from '@angular/forms';

const PACKAGE_SUCCESS_CLASS = 'pkg-success';
const PACKAGE_WARNING_CLASS = 'pkg-warn';
const PACKAGE_ERROR_CLASS = 'pkg-error';

@Component({
    selector: 'app-archbuild',
    templateUrl: './archbuild.component.html',
    styleUrls: ['./archbuild.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule]
})
export class ArchbuildComponent implements OnInit {

  state: ArchBuildPackageDto[] = [];

  createNew: ArchBuildPackageCreationDto = {
    name: '',
    url: ''
  };

  private expanded: string[] = [];
  private builds = new Map<number, ArchBuildPackageBuildDto[]>();
  private steps = new Map<number, ArchBuildPackageBuildStepDto[]>();

  constructor(
    private archbuildService: ArchbuildService,
    private notificationService: ToastrService,
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  loadData() {
    this.archbuildService.getStatus().subscribe({
      next: res => this.state = res,
      error: err => this.notificationService.error('failed to retrieve data ' + JSON.stringify(err))
    });
  }

  isExpanded(expandKey: string): boolean {
    return this.expanded.includes(expandKey);
  }

  toggleExpanded(expandKey: string) {
    if (this.isExpanded(expandKey)) {
      this.expanded = this.expanded.filter(it => it !== expandKey);
    } else {
      this.expanded.push(expandKey);
    }
  }

  togglePackage(pkg: ArchBuildPackageDto) {
    this.toggleExpanded('' + pkg.id);
    if (this.isExpanded('' + pkg.id) && !this.builds.has(pkg.id)) {
      this.loadBuilds(pkg.id);
    }
  }

  toggleBuild(pkg: ArchBuildPackageDto, build: ArchBuildPackageBuildDto) {
    const key = `${pkg.id}.${build.id}`;
    this.toggleExpanded(key);
    if (this.isExpanded(key) && !this.steps.has(build.id)) {
      this.loadSteps(build.id);
    }
  }

  getBuildsFor(pkg: ArchBuildPackageDto): ArchBuildPackageBuildDto[] {
    return this.builds.get(pkg.id) ?? [];
  }

  getStepsFor(build: ArchBuildPackageBuildDto): ArchBuildPackageBuildStepDto[] {
    return this.steps.get(build.id) ?? [];
  }

  private loadBuilds(pkgId: number) {
    this.archbuildService.getBuilds(pkgId).subscribe({
      next: res => this.builds.set(pkgId, res),
      error: err => this.notificationService.error('failed to retrieve builds ' + JSON.stringify(err))
    });
  }

  private loadSteps(buildId: number) {
    this.archbuildService.getBuildSteps(buildId).subscribe({
      next: res => this.steps.set(buildId, res),
      error: err => this.notificationService.error('failed to retrieve build steps ' + JSON.stringify(err))
    });
  }

  getProjectStyles(pkg: ArchBuildPackageDto): string[] {
    const lastBuild = pkg.lastBuild;
    if (!lastBuild) {
      return [];
    }
    if (lastBuild.success) {
      return [PACKAGE_SUCCESS_CLASS];
    } else if (lastBuild.silenced) {
      return [PACKAGE_WARNING_CLASS];
    } else {
      return [PACKAGE_ERROR_CLASS];
    }
  }

  getBuildStyles(build: ArchBuildPackageBuildDto): string[] {
    if (build.success) {
      return [PACKAGE_SUCCESS_CLASS];
    } else if (build.silenced) {
      return [PACKAGE_WARNING_CLASS];
    } else {
      return [PACKAGE_ERROR_CLASS];
    }
  }

  getStepStyles(step: ArchBuildPackageBuildStepDto): string[] {
    if (step.success) {
      return [PACKAGE_SUCCESS_CLASS];
    } else {
      return [PACKAGE_ERROR_CLASS];
    }
  }

  changedName() {
    if (this.createNew.name) {
      this.createNew.url = `https://aur.archlinux.org/${this.createNew.name}.git`;
    } else {
      this.createNew.url = '';
    }
  }

  createNewNow() {
    this.archbuildService.createNewProject(this.createNew).subscribe({
      next: () => {
        this.notificationService.success('created');
        this.loadData();
      },
      error: err => this.notificationService.error('failed to create: ' + JSON.stringify(err))
    });
  }

  buildNow(pkg: ArchBuildPackageDto) {
    this.archbuildService.manuallyBuild(pkg.id).subscribe({
      next: () => {
        this.notificationService.success('successfully triggered');
        this.loadData();
        if (this.builds.has(pkg.id)) {
          this.loadBuilds(pkg.id);
        }
      },
      error: err => this.notificationService.error('failed to trigger: ' + JSON.stringify(err))
    });
  }

  silence(pkg: ArchBuildPackageDto, build: ArchBuildPackageBuildDto) {
    this.archbuildService.ignoreFailedBuild(build.id).subscribe({
      next: () => {
        this.notificationService.success('silenced successfully');
        build.silenced = true;
        if (pkg.lastBuild?.id === build.id) {
          pkg.lastBuild.silenced = true;
        }
      },
      error: err => {
        this.notificationService.error('failed to silence: ' + JSON.stringify(err));
        console.error('failed to silence: ', err);
      }
    });
  }
}
